import { useEffect, useState } from 'react'

import {
  getPatchReceptionCallHistoryMutationKey,
  useCreateMeeting,
  usePatchReceptionCallHistory,
} from '@reception-web/common/api/app'
import {
  HISTORY_CALLER_STATUS,
  HISTORY_RECEIVER_STATUS,
} from '@reception-web/common/consts'
import { Card, Flex, Input, List } from 'antd'
import { useTranslation } from 'react-i18next'
import { LuPhoneIncoming, LuPhoneOutgoing } from 'react-icons/lu'
import { MdOutlineVideocam } from 'react-icons/md'
import styled from 'styled-components'

import {
  useAosToken,
  useAttendeePresenceListener,
  useCallStatus,
  useHistoryApi,
  useMeetingContext,
} from '../../hooks'

const Container = styled(Card)`
  width: 20%;
`

const StyledListItem = styled(List.Item)`
  &:hover {
    background: #f0f0f0;
  }
`

const StyledListItemMeta = styled(List.Item.Meta)`
  padding: 0 10px;
`

const StyledCam = styled(MdOutlineVideocam)`
  color: light-blue;
  padding: 0 5px;
  cursor: pointer;
`

export const MeetingHistory = () => {
  const { setStatus } = useCallStatus()
  const aosToken = useAosToken()
  const { data: histories } = useHistoryApi({ aosToken })
  const { trigger: createMeeting } = useCreateMeeting({
    axios: { headers: { Authorization: `Token ${aosToken}` } },
  })
  const {
    setMeeting,
    setAttendee,
    setInformation,
    attendee,
    meeting,
    setIsRedial,
  } = useMeetingContext()
  const [search, setSearch] = useState('')
  const { trigger: patchReceptionCallHistory } = usePatchReceptionCallHistory(
    meeting?.MeetingId ?? '',
    {
      swr: {
        swrKey: getPatchReceptionCallHistoryMutationKey(
          meeting?.MeetingId ?? ''
        )[0],
      },
      axios: {
        headers: {
          Authorization: `Token ${aosToken}`,
        },
      },
    }
  )

  const {
    t,
    i18n: { language },
  } = useTranslation('translation', {
    keyPrefix: 'components.MeetingHistory',
  })
  const translationKey = language === 'ja' ? 'ja' : 'en'

  useAttendeePresenceListener({
    selfId: attendee?.AttendeeId ?? '',
    onJoin: async () => {
      await patchReceptionCallHistory({
        caller_status: HISTORY_CALLER_STATUS.THE_CALL_ESTABLISHED,
      })
      setStatus('calling')
    },
  })

  useEffect(() => {
    if (meeting) {
      patchReceptionCallHistory({
        caller_status: HISTORY_CALLER_STATUS.CALLING,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting])

  useEffect(() => {
    histories?.forEach(async (history) => {
      if (
        history.meeting_id === meeting?.MeetingId &&
        history.receiver_status === HISTORY_RECEIVER_STATUS.REJECT
      ) {
        await patchReceptionCallHistory({
          caller_status: HISTORY_CALLER_STATUS.THE_CALL_NOT_ESTABLISHED,
        })
        alert(t('canceled'))
        window.location.reload()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [histories, meeting?.MeetingId])

  const handleItemClick = async ({
    property,
    deviceId,
    deviceName,
    propertySlug,
  }: {
    property: string
    deviceId: string
    deviceName: string
    propertySlug: string
  }) => {
    setStatus('callback')
    const data = await createMeeting({ device_id: deviceId })
    setMeeting(data.data.Meeting)
    const crewAttendee = data.data.Attendees.find(
      (attendee) => !attendee.ExternalUserId.match(propertySlug)
    )
    setAttendee(crewAttendee ?? null)
    setInformation({
      property,
      deviceName,
    })
    setIsRedial(true)
  }

  const filteredHistories = histories
    ?.filter(
      (history) =>
        history.translations?.property_translations?.[
          translationKey
        ]?.name?.includes(search)
    )
    .map((history) => ({
      ...history,
      isGuestTimeout:
        history.call_type === 'call_from_device' &&
        history.caller_status === HISTORY_CALLER_STATUS.TIMEOUT,
      status:
        history.call_type === 'call_from_device'
          ? t(`guestStatus.${history.caller_status}`)
          : t(`crewStatus.${history.caller_status}`),
    }))

  return (
    <Container
      title={t('title')}
      styles={{
        header: { textAlign: 'center' },
        body: { maxHeight: '80vh', overflow: 'scroll' },
      }}
    >
      <Input
        placeholder="Search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <List
        dataSource={filteredHistories}
        renderItem={(history, index) => (
          <StyledListItem
            key={index}
            actions={[
              history.expiredRedial ? null : (
                <StyledCam
                  key={index}
                  size={30}
                  color="#00a"
                  onClick={() =>
                    handleItemClick({
                      property:
                        history.translations?.property_translations?.[
                          translationKey
                        ]?.name ?? '',
                      deviceId: history.reception_device_id ?? '',
                      deviceName:
                        history.translations?.device_translations?.[
                          translationKey
                        ]?.name ?? '',
                      propertySlug: history.property_slug ?? '',
                    })
                  }
                />
              ),
            ]}
          >
            <StyledListItemMeta
              title={
                <Flex gap={10} align="center">
                  {history.call_type === 'call_from_device' ? (
                    <LuPhoneIncoming size={20} color="green" />
                  ) : (
                    <LuPhoneOutgoing size={20} color="blue" />
                  )}
                  {history.translations?.property_translations?.[translationKey]
                    ?.name ?? ''}
                </Flex>
              }
              description={
                <div>
                  <div>
                    {history.translations?.device_translations?.[translationKey]
                      ?.name ?? ''}
                  </div>
                  <div
                    style={{
                      color: history.isGuestTimeout
                        ? 'red'
                        : 'rgba(0, 0, 0, 0.45)',
                      fontWeight: 'bold',
                    }}
                  >
                    {history.status}
                  </div>
                  <div>{history.created}</div>
                </div>
              }
            />
          </StyledListItem>
        )}
      />
    </Container>
  )
}
